import { useEffect, useState, useRef, useLayoutEffect } from 'react'
import { Check, X, ChevronDown, ChevronUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/ui/components/common/Button'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { RichTextEditor } from '@/ui/components/common/RichTextEditor'
import { classNames } from '@/ui/utils/main'

interface NotesPanelProps {
  /** Current saved markdown content (null / undefined = no notes yet). */
  notes?: string | null
  /** Called when the user clicks "Enregistrer". Receives the markdown string. */
  onSave: (notes: string) => Promise<void>
  /** Whether the current user may edit notes (streamers / admins / investigation author). */
  canEdit: boolean
  /** Edit mode is controlled from the parent (button lives next to the section title). */
  isEditing: boolean
  onEditingChange: (value: boolean) => void
}

/**
 * Panel chrome (expand/collapse preview, save/cancel actions, edit gating)
 * around the shared `RichTextEditor` / `MarkdownView` pair. Owns none of the
 * editor's internal markdown reconciliation — `RichTextEditor` handles that.
 */
export function NotesPanel({
  notes,
  onSave,
  canEdit,
  isEditing,
  onEditingChange,
}: NotesPanelProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [draft, setDraft] = useState(notes ?? '')

  // Reset the draft to the last saved value whenever the parent opens edit mode.
  useEffect(() => {
    if (isEditing) setDraft(notes ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync on entering edit mode, not on every `notes` change
  }, [isEditing])

  // Ref on the preview container: detects whether rendered content exceeds
  // the 5-line clip so the "Voir plus" button is only shown when needed.
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const element = contentRef.current
    if (!element || isExpanded) return
    setIsOverflowing(element.scrollHeight > element.clientHeight)
  }, [notes, isExpanded])

  const hasNotes = !!(notes && notes.trim().length > 0)
  const notesText = notes ?? ''

  // Nothing to show for anonymous viewers when there are no notes
  if (!hasNotes && !canEdit) return null

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await onSave(draft)
      onEditingChange(false)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setDraft(notes ?? '')
    onEditingChange(false)
  }

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {isEditing ? (
        /* ── Edit mode ── */
        <>
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            disabled={isSaving}
            onUploadError={(message) => toast.error(message)}
          />
          <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-border bg-muted/20">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="w-3.5 h-3.5 mr-1" />
              {t('editor.cancel')}
            </Button>
            <Button type="button" size="sm" onClick={handleSave} isLoading={isSaving}>
              <Check className="w-3.5 h-3.5 mr-1" />
              {t('editor.save')}
            </Button>
          </div>
        </>
      ) : (
        /* ── View mode ── */
        <div className="p-4">
          {hasNotes ? (
            <>
              {/* Preview: clipped to ~5 lines. Expanded: scrollable ~15 lines. */}
              <div
                ref={contentRef}
                className={classNames(
                  'break-words',
                  !isExpanded && 'max-h-[140px] overflow-hidden',
                  isExpanded && 'max-h-[400px] overflow-y-auto pr-1',
                )}
              >
                <MarkdownView content={notesText} />
              </div>
              {(isOverflowing || isExpanded) && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    className="flex items-center gap-1 text-sm text-white hover:text-white/70 transition-colors"
                  >
                    {isExpanded ? t('lead_detail.fact_see_less') : t('lead_detail.fact_see_more')}
                    {isExpanded ? (
                      <ChevronUp className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground italic">{t('editor.no_notes')}</p>
          )}
        </div>
      )}
    </div>
  )
}
